import {
  IOrderRefundData,
  isSettledPaymentStatus,
  PaymentStatusKey
} from './order.dto';

/**
 * Shared order-payment math and routing for every client that charges or
 * refunds (admin-web, emr-web, processors). Mirrors `OrdersManager` /
 * `OrderPaymentService.processorForOrder` so a UI can validate before calling
 * the API instead of discovering the cap through a 500.
 */

/** Round to cents. Used by charge, deferred, and refund amount math. */
export function toDollars(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Amount to charge now for an order.
 *
 * For partial_paid, derive remaining from current totalAmount minus already
 * captured payment.amount so EMR price/insurance edits stay correct even when
 * payment.delayedAmount is stale.
 */
export function resolveOrderChargeAmount(order: {
  totalAmount?: number;
  payment?: {
    status?: string;
    amount?: number;
    delayedAmount?: number;
  } | null;
}): number {
  const total = toDollars(order.totalAmount || 0);
  if (order.payment?.status === 'partial_paid') {
    const alreadyPaid = order.payment?.amount || 0;
    return Math.max(0, toDollars(total - alreadyPaid));
  }
  return total;
}

/**
 * Recompute payment.delayedAmount after EMR changes order totalAmount.
 * Returns undefined when the payment status does not carry a deferred balance.
 */
export function resolveDeferredAmountAfterTotalChange(
  payment:
    | {
        status?: string;
        amount?: number;
        delayedAmount?: number;
      }
    | null
    | undefined,
  newTotalAmount: number
): number | undefined {
  if (!payment?.status) {
    return payment?.delayedAmount;
  }

  const total = toDollars(newTotalAmount || 0);

  if (payment.status === 'partial_paid') {
    return Math.max(0, toDollars(total - (payment.amount || 0)));
  }

  if (
    payment.status === 'delayed_capture' ||
    payment.status === 'adjudicated'
  ) {
    return total;
  }

  return payment.delayedAmount;
}

/**
 * The gateway holding this order's money, resolved the way the API routes
 * charges, refunds, and retries (`OrderPaymentService.processorForOrder`).
 */
export function resolveOrderPaymentGateway(
  order: RefundableOrder
): string | undefined {
  if (order.payment?.gateway) return order.payment.gateway;
  return order.version !== 2 ? 'stripe' : undefined;
}

export type RefundReason = NonNullable<IOrderRefundData['reason']>;

export const REFUND_REASONS: { key: RefundReason; label: string }[] = [
  { key: 'requested_by_customer', label: 'Requested by customer' },
  { key: 'duplicate', label: 'Duplicate' },
  { key: 'fraudulent', label: 'Fraudulent' }
];

/** Gateway refund statuses that will not change again. */
const TERMINAL_REFUND_STATUSES = new Set([
  'succeeded',
  'failed',
  'canceled',
  'cancelled'
]);

export function isTerminalRefundStatus(status?: string): boolean {
  return !!status && TERMINAL_REFUND_STATUSES.has(status);
}

/** Terminal but not successful — the refund will not happen without a new attempt. */
export function isFailedRefundStatus(status?: string): boolean {
  return isTerminalRefundStatus(status) && status !== 'succeeded';
}

/** The order fields refund amount math reads. Deliberately narrower than IOrder. */
export interface RefundableOrder {
  totalAmount?: number;
  creditUsedAmount?: number;
  version?: number;
  payment?: {
    status?: string;
    paymentId?: string;
    refundAmount?: number;
    gateway?: string;
  } | null;
}

/** Remaining refundable dollars: (total + credit used) − already refunded. */
export function remainingRefundableAmount(order: RefundableOrder): number {
  const totalPaid = toDollars(
    (order.totalAmount || 0) + (order.creditUsedAmount || 0)
  );
  const already = toDollars(order.payment?.refundAmount || 0);
  return toDollars(Math.max(0, totalPaid - already));
}

export function canRefundOrder(order: RefundableOrder): boolean {
  const status = order.payment?.status;
  if (!order.payment?.paymentId) return false;
  if (!isSettledPaymentStatus(status as PaymentStatusKey)) return false;
  return remainingRefundableAmount(order) > 0;
}

export function paymentGatewayLabel(gateway?: string): string {
  switch (gateway) {
    case 'whop':
      return 'Whop';
    case 'nmi':
      return 'NMI';
    case 'stripe':
      return 'Stripe';
    default:
      return gateway
        ? gateway.charAt(0).toUpperCase() + gateway.slice(1)
        : 'payment processor';
  }
}

export function parseRefundAmount(
  raw: string | number | null | undefined,
  remaining: number
): { amount: number } | { error: string } {
  const t = typeof raw === 'string' ? raw.trim() : raw;
  if (t === '' || t == null) return { amount: remaining };
  const n = Number(t);
  if (!Number.isFinite(n)) return { error: 'Enter a valid amount.' };
  const amount = toDollars(n);
  if (amount <= 0) return { error: 'Refund amount must be greater than 0.' };
  if (amount > remaining + 0.001) {
    return {
      error: `Amount exceeds remaining refundable $${remaining.toFixed(2)}.`
    };
  }
  return { amount };
}

/**
 * Omit `amount` when refunding the full remaining balance so the API takes its
 * own full-refund path instead of comparing floats.
 */
export function buildOrderRefundBody(
  amount: number,
  remaining: number,
  reason: RefundReason
): IOrderRefundData {
  const body: IOrderRefundData = { reason };
  if (toDollars(amount) + 0.001 < remaining) {
    body.amount = toDollars(amount);
  }
  return body;
}
